/**
 * Candidate picker for image-based product lookup.
 *
 * Mounted as an overlay inside the CameraBarcodeScanner after the cashier
 * taps "Match by image". Shows the ranked candidate list with confidence
 * bars; the cashier picks one and we add it to the cart.
 *
 * Empty-state "Add as new product" CTA is MANAGER+ only — same rule as the
 * Quick-Add page that creates the underlying product.
 *
 * Visual design:
 *   - GLASS panel (three-principle compliant: no borders, glass depth)
 *   - Tabular-nums on price / stock / confidence (Mono Data principle)
 *   - Confidence tiered tri-colour: teal (strong) → amber (medium) → slate (weak)
 *   - Top candidate gets a "Best match" ribbon so the cashier's eye anchors
 *     on the winner in <200ms at a busy counter
 */

import { useEffect, useRef } from 'react';
import { Alert, Button, Spinner } from '@bbpos/ui';
import { GLASS, cn } from '@/constants/design-tokens';
import { ROLE_LEVELS } from '@/lib/quote-constants';
import { formatCurrency } from '@/lib/format';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useAuthStore } from '@/stores/use-auth-store';
import type { ImageLookupCandidate } from '@/services/products.service';

// ─── Confidence tiering ─────────────────────────────────────────────────────
// Thresholds match the ImageLookupService scoring (distance ∈ [0, 12]):
//   strong : confidence ≥ 0.92  (distance ≤ 5)    — cashier can click immediately
//   medium : confidence ≥ 0.86  (distance ≤ 9)    — likely same SKU, glance-check
//   weak   : confidence < 0.86                     — verify carefully

type ConfidenceTier = 'strong' | 'medium' | 'weak';

function tierFor(confidence: number): ConfidenceTier {
    if (confidence >= 0.92) return 'strong';
    if (confidence >= 0.86) return 'medium';
    return 'weak';
}

const TIER_CLASSES: Record<ConfidenceTier, { bar: string; text: string; ring: string }> = {
    strong: {
        bar: 'bg-gradient-to-r from-teal-500 to-emerald-500',
        text: 'text-teal-700 dark:text-teal-300',
        ring: 'focus:ring-teal-500',
    },
    medium: {
        bar: 'bg-gradient-to-r from-amber-400 to-amber-500',
        text: 'text-amber-700 dark:text-amber-300',
        ring: 'focus:ring-amber-500',
    },
    weak: {
        bar: 'bg-gradient-to-r from-slate-400 to-slate-500',
        text: 'text-slate-600 dark:text-slate-300',
        ring: 'focus:ring-slate-500',
    },
};

// ─── Icons ──────────────────────────────────────────────────────────────────

function CloseIcon() {
    return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function ScanIcon() {
    return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    );
}

function EmptyCameraIcon() {
    return (
        <svg className="w-10 h-10 text-teal-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
        </svg>
    );
}

export interface ImageMatchCandidatePickerProps {
    open: boolean;
    isLoading: boolean;
    error?: string | null;
    candidates: ImageLookupCandidate[];
    onPick: (candidate: ImageLookupCandidate) => void;
    onRetake: () => void;
    onAddAsNew?: () => void;
    onClose: () => void;
}

export function ImageMatchCandidatePicker({
    open,
    isLoading,
    error,
    candidates,
    onPick,
    onRetake,
    onAddAsNew,
    onClose,
}: ImageMatchCandidatePickerProps) {
    const profile = useAuthStore((s) => s.profile);
    const currency = useTenantCurrency();
    const isManagerOrAbove = !!profile?.role && ROLE_LEVELS[profile.role] >= ROLE_LEVELS.MANAGER;

    const listRef = useRef<HTMLDivElement | null>(null);

    // Auto-focus the top candidate when the list mounts so keyboard users
    // can press Enter immediately to pick the best match.
    useEffect(() => {
        if (!open || isLoading || candidates.length === 0) return;
        const first = listRef.current?.querySelector<HTMLButtonElement>('[data-candidate-index="0"]');
        first?.focus();
    }, [open, isLoading, candidates.length]);

    // Esc closes the picker
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [open, onClose]);

    function handleListKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        if (candidates.length === 0) return;
        const target = e.target as HTMLElement;
        const indexAttr = target.getAttribute('data-candidate-index');
        if (indexAttr === null) return;
        const current = Number(indexAttr);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = Math.min(candidates.length - 1, current + 1);
            listRef.current
                ?.querySelector<HTMLButtonElement>(`[data-candidate-index="${next}"]`)
                ?.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = Math.max(0, current - 1);
            listRef.current
                ?.querySelector<HTMLButtonElement>(`[data-candidate-index="${prev}"]`)
                ?.focus();
        }
    }

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in p-0 sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Image match results"
        >
            <div
                className={cn(
                    GLASS,
                    'w-full sm:w-[640px] max-h-[92vh] flex flex-col animate-slide-up',
                )}
            >
                {/* ── Header ─────────────────────────────────────────── */}
                <div className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 text-white flex items-center justify-center shadow-[0_6px_16px_-8px_rgba(20,184,166,0.55)] shrink-0">
                            <ScanIcon />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent truncate">
                                Match by image
                            </h2>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                                {isLoading
                                    ? 'Looking for visual matches…'
                                    : candidates.length > 0
                                        ? `${candidates.length} candidate${candidates.length === 1 ? '' : 's'} — pick the right one`
                                        : 'No matching products found'}
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="min-h-[44px] min-w-[44px]"
                        aria-label="Close image match"
                    >
                        <CloseIcon />
                    </Button>
                </div>

                {/* ── Body ───────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-5 pb-5">
                    {isLoading && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            <Spinner />
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">Analyzing image…</p>
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="py-4">
                            <Alert variant="danger" title="Image lookup failed" description={error} />
                            <div className="mt-4 flex justify-end">
                                <Button variant="secondary" onClick={onRetake} className="min-h-[44px]">
                                    Retake photo
                                </Button>
                            </div>
                        </div>
                    )}

                    {!isLoading && !error && candidates.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center shadow-[0_10px_30px_-18px_rgba(20,184,166,0.55)]">
                                <EmptyCameraIcon />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                                    No matching products found
                                </p>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-xs">
                                    Try moving closer, improving lighting, or using a clearer angle.
                                </p>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button variant="secondary" onClick={onRetake} className="min-h-[44px]">
                                    Retake photo
                                </Button>
                                {isManagerOrAbove && onAddAsNew && (
                                    <Button variant="primary" onClick={onAddAsNew} className="min-h-[44px]">
                                        Add as new product
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {!isLoading && !error && candidates.length > 0 && (
                        <div
                            ref={listRef}
                            onKeyDown={handleListKeyDown}
                            className="space-y-2"
                            role="listbox"
                            aria-label="Candidate matches"
                        >
                            {candidates.map((candidate, idx) => {
                                const confidencePct = Math.round(candidate.confidence * 100);
                                const tier = tierFor(candidate.confidence);
                                const tierClasses = TIER_CLASSES[tier];
                                const isTop = idx === 0;
                                return (
                                    <button
                                        key={candidate.product.id}
                                        type="button"
                                        data-candidate-index={idx}
                                        onClick={() => onPick(candidate)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onPick(candidate);
                                            }
                                        }}
                                        aria-label={`${candidate.product.name}, SKU ${candidate.product.sku}, ${formatCurrency(candidate.product.price, currency)}, confidence ${confidencePct}%${isTop ? ', best match' : ''}`}
                                        className={cn(
                                            'relative w-full flex items-center gap-4 p-3 rounded-xl text-left',
                                            'transition-all duration-150',
                                            'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent',
                                            tierClasses.ring,
                                            'min-h-[72px]',
                                            isTop
                                                ? 'bg-gradient-to-r from-teal-50/95 to-emerald-50/70 dark:from-teal-900/30 dark:to-emerald-900/15 shadow-[0_10px_28px_-18px_rgba(20,184,166,0.55)] hover:shadow-[0_14px_32px_-18px_rgba(20,184,166,0.75)]'
                                                : 'bg-white/80 dark:bg-neutral-800/60 hover:bg-teal-50/70 dark:hover:bg-teal-900/15',
                                        )}
                                    >
                                        {/* "Best match" ribbon — anchors the cashier's eye on candidate #1 */}
                                        {isTop && (
                                            <span className="absolute -top-2 left-3 text-[10px] font-bold tracking-wider uppercase bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-2 py-0.5 rounded-md shadow-[0_4px_10px_-4px_rgba(20,184,166,0.7)]">
                                                Best match
                                            </span>
                                        )}

                                        {/* Thumbnail */}
                                        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-teal-100 to-emerald-100 dark:from-teal-900/40 dark:to-emerald-900/40 flex items-center justify-center">
                                            {candidate.product.image_url ? (
                                                <img
                                                    src={candidate.product.image_url}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <span className="text-xs font-semibold text-teal-700 dark:text-teal-300 tabular-nums">
                                                    {candidate.product.sku.slice(0, 4)}
                                                </span>
                                            )}
                                        </div>

                                        {/* Name + meta */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate">
                                                {candidate.product.name}
                                            </p>
                                            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate tabular-nums">
                                                {candidate.product.sku} · Stock {candidate.product.stock_quantity}
                                            </p>
                                            <p className="text-sm font-semibold text-neutral-900 dark:text-white tabular-nums mt-0.5">
                                                {formatCurrency(candidate.product.price, currency)}
                                            </p>
                                        </div>

                                        {/* Confidence bar — tri-tier colour anchors strength visually */}
                                        <div className="w-24 flex-shrink-0">
                                            <div className={cn('text-[11px] font-semibold tabular-nums text-right mb-1', tierClasses.text)}>
                                                {confidencePct}%
                                            </div>
                                            <div className="h-1.5 w-full rounded-full bg-neutral-200/70 dark:bg-neutral-700/70 overflow-hidden">
                                                <div
                                                    className={cn('h-full rounded-full', tierClasses.bar)}
                                                    style={{ width: `${confidencePct}%` }}
                                                />
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}

                            <div className="pt-3 flex justify-between items-center">
                                <Button variant="ghost" size="sm" onClick={onRetake} className="min-h-[44px]">
                                    Retake photo
                                </Button>
                                <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                                    ↑↓ navigate · Enter to add · Esc to close
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
